import { FC, MutableRefObject, useEffect, useState } from 'react';
import { VideoPlayerEvents } from '@sendspark/player/dist/VideoPlayer';
import { Campaign } from '@interfaces';
import { TranscriptSource } from '@hooks';
 import VideoPlayerIds from '@components/VideoPlayer/VideoPlayerIds';
import TranscriptPanel from './index';

interface Props {
  campaign: Campaign;
  // Player events from the share <Player> (seekTo lives here).
  playerEventsRef: MutableRefObject<VideoPlayerEvents | null>;
  // When rendered inside the SidePanel tab switcher, omit the panel's own card + header (the tabs
  // play that role) and signal which tab is selected so the active-line scroll can recover from
  // display: none.
  embedded?: boolean;
  isActive?: boolean;
  // Forwarded to TranscriptPanel so the SidePanel tab can badge the source actually loaded.
  onSourceChange?: (source: TranscriptSource) => void;
}

// The player renders its <video> with this id (from @sendspark/player's VideoPlayerIds).
const PLAYER_VIDEO_ID = VideoPlayerIds.VideoPlayer;
const RESOLVE_INTERVAL_MS = 250;
const RESOLVE_MAX_ATTEMPTS = 40; // ~10s, then give up (transcript still renders, just no active line)

// Bridges the share player to the transcript panel: locates the player's <video> element (mounted
// asynchronously by @sendspark/player) so useTranscript can follow playback, and routes line
// clicks through the controller's seekTo.
const TranscriptSection: FC<Props> = ({ campaign, playerEventsRef, embedded, isActive, onSourceChange }) => {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (video) return undefined;
    let attempts = 0;
    const id = window.setInterval(() => {
      attempts += 1;
      const el = document.getElementById(PLAYER_VIDEO_ID) as HTMLVideoElement | null;
      if (el) {
        setVideo(el);
        window.clearInterval(id);
      } else if (attempts >= RESOLVE_MAX_ATTEMPTS) {
        window.clearInterval(id);
      }
    }, RESOLVE_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [video]);

  const handleSeek = (time: number) => {
    // seekTo returns a promise; rejection (e.g. superseded seek) is non-fatal here.
    void playerEventsRef.current?.seekTo(time)?.catch(() => undefined);
  };

  return (
    <TranscriptPanel
      campaign={campaign}
      video={video}
      onSeek={handleSeek}
      fileName={campaign?.meta?.video?.name || 'transcript'}
      embedded={embedded}
      isActive={isActive}
      onSourceChange={onSourceChange}
    />
  );
};

export default TranscriptSection;
